import { ActionTracker, SegmentTracker } from '@varsitytutors/event-tracker';
import { getSegmentNonDestinationKey } from './credentials';
import {
  buildActionTrackerPage,
  buildSegmentPageEvent,
} from './helpers/page_event';

let segmentTrackerInstances = {};
let actionTracker;

export function enabled() {
  const c = window.components;
  return !!(c && c.action_tracker && c.action_tracker.endpoint);
}

export function pageSegment(writeKey, args) {
  const segmentInstance = getSegmenTracker(writeKey);
  if (!segmentInstance) {
    return;
  }
  return segmentInstance.page(buildSegmentPageEvent(args));
}

export function trackSegment(eventName, args, writeKey, useTransform = true) {
  const segmentInstance = getSegmenTracker(writeKey);
  if (!segmentInstance) {
    return;
  }
  let params = defaultParams(eventName, args);
  if (useTransform) params = transformParams(eventName, args);
  params.applicationId = window?.components?.action_tracker?.application_id;
  return segmentInstance.track({
    eventName,
    properties: params,
  });
}

export function track(eventName, args) {
  if (setup()) {
    return actionTracker.track(transformParams(eventName, args));
  }
}

export function defaultParams(eventName, args) {
  const params = {
    customEventData: {
      action: eventName,
    },
    ...args,
  };
  return params;
}

// Transforms params from a flat structure to the format that is required
// of for submission to vt-events via actiontracker
export function transformParams(eventName, args) {
  const search_params = searchParams(args);
  let params = {
    page: buildActionTrackerPage(args),
    domEventData: domParams(args),
    customEventData: customParams(eventName, args),
    pageRequestId: args.request_id || 'undefined',
    window: windowParams(),
  };

  // optional fields
  Object.assign(
    params,
    args.leadFormId != null
      ? { leadFormEventData: leadFormParams(args) }
      : null,
    Object.keys(search_params).length > 0
      ? { course_search: search_params }
      : null
  );

  return params;
}

export function windowParams() {
  let params = {
    height: window.innerHeight,
    width: window.innerWidth,
    visibleElements: [],
  };

  return params;
}

export function describeElement(el) {
  // Values always present
  let params = {
    classes: el.className.split(' ').filter((str) => str.length > 0),
  };

  // Optional values on a per element basis
  Object.assign(
    params,
    el.dataset.component != null ? { component: el.dataset.component } : null,
    el.dataset.label != null ? { label: el.dataset.label } : null
  );

  return params;
}

/**
 * Gets the content of a meta tag from the page
 * @param {String} name - Name of meta tag
 * @return {String|null} The content of the meta tag
 */
function getMeta(name) {
  const meta = document.querySelector(`meta[name=${name}]`);
  return meta ? meta.content : null;
}

/**
 * Gets the content of a meta tag from the page
 * @param {String} property - Property value of meta tag
 * @return {String|null} The content of the meta tag
 */
function getMetaProperty(property) {
  const meta = document.querySelector(`meta[property=${property}]`);
  return meta ? meta.content : null;
}

// Create the page params based on the values passed into args
export function pageParams(args) {
  let params = {
    url: location.href.split('#')[0],
    visitorId: args.visitor_id,
    divisionId: args.division_id || 'VT::VT',
    userAgent: navigator.userAgent,
    contentVersion: args.content_version || getMeta('content-version'),
    nspSlug: getMetaProperty('nsp-slug') || '',
    nspSlugType: getMetaProperty('nsp-slug-type') || '',
    cache: (args.cache || 'none').toLocaleLowerCase(),
    applicationTime: args.applicationTime,
    firstContentfulPaintTime: args.firstContentfulPaintTime,
  };

  // Set optional params if they are present
  const optionals = {
    clientUuid: 'client_uuid',
    entity: 'entity',
    sessionId: 'session_id',
    userId: 'user_id',
  };
  Object.keys(optionals).forEach((key) => {
    if (args[optionals[key]]) {
      params[key] = args[optionals[key]];
    }
  });

  if (args.ab_test_experiment && args.ab_test_variant) {
    params.tests = [
      {
        experimentId: args.ab_test_experiment,
        variantId: args.ab_test_variant,
      },
    ];
  }

  return params;
}

/* eslint complexity:off */
// Create the DOM params based on the values passed into args
export function domParams(args) {
  // Required values
  let params = {
    target: args.target,
  };

  let includeDataAttributes =
    args.dataAttributes &&
    typeof args.dataAttributes == 'object' &&
    Object.keys(args.dataAttributes).length > 0;

  // Optional values
  Object.assign(
    params,
    args.className ? { classes: args.className.split(' ') } : null,
    args.href ? { href: args.href } : null,
    args.text ? { text: args.text } : null,
    args.fieldValue ? { value: args.fieldValue } : null,
    args.fieldName ? { name: args.fieldName } : null,
    includeDataAttributes ? { data: args.dataAttributes } : null
  );

  return params;
}
/* eslint complexity:error */

// Creates the custom params based on the values passed into args
export function customParams(eventName, args) {
  // Required Values
  let params = {
    action: eventName,
  };

  // Optional values
  Object.assign(
    params,
    args.category != null ? { category: args.category } : null,
    args.label != null ? { label: args.label } : null,
    args.value != null ? { value: args.value } : null
  );

  return params;
}

// Creates the search params based on the values passed into args
export function searchParams(args) {
  let params = {};

  Object.assign(
    params,
    args.query != null && typeof args.query === 'string'
      ? { query: args.query }
      : null,
    args.filters != null && typeof args.filters === 'object'
      ? { filters: args.filters }
      : null,
    args.page != null && typeof args.page === 'object'
      ? { page: args.page }
      : null,
    args.result_uuids != null && Array.isArray(args.result_uuids)
      ? { result_uuids: args.result_uuids }
      : null
  );

  return params;
}

// Create the DOM params based on the values passed into args
export function leadFormParams(args) {
  // Required values
  let params = {
    id: args.leadFormId,
  };

  // Optional values
  Object.assign(params, args.panelId != null ? { panel: args.panelId } : null);

  return params;
}

export function getSegmenTracker(segmentWriteKey) {
  const nonDestinationWriteKey = getSegmentNonDestinationKey();
  if (!segmentWriteKey || !nonDestinationWriteKey) return null;
  segmentTrackerInstances[segmentWriteKey] =
    segmentTrackerInstances[segmentWriteKey] ||
    new SegmentTracker(segmentWriteKey, {
      useHttpClient: true,
      nonDestinationWriteKey,
    });
  return segmentTrackerInstances[segmentWriteKey];
}

export function setup() {
  if (!enabled()) return false;
  if (actionTracker) return true;

  actionTracker = new ActionTracker({
    endpoint: window.components.action_tracker.endpoint,
    applicationId: window.components.action_tracker.application_id,
  });

  return true;
}
